import { useEffect, useState } from "react";

const KEY = "nutrimart_products_cache_v1";
const META_KEY = "nutrimart_products_cache_meta_v1";

export interface OfflineCacheMeta {
  cachedAt: number;
  count: number;
}

/**
 * Persists last successful products fetch to localStorage so the app
 * can show products when offline. Read on init, write whenever fresh
 * data arrives.
 */
export function readOfflineProducts<T = unknown>(): T[] | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as T[];
  } catch {
    return null;
  }
}

export function getOfflineCacheMeta(): OfflineCacheMeta | null {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function writeOfflineProducts<T = unknown>(products: T[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(products));
    const meta: OfflineCacheMeta = { cachedAt: Date.now(), count: products.length };
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch (e) {
    // Quota exceeded or storage disabled; silently ignore.
    console.warn("[offline-cache] could not persist products:", e);
  }
}

/** Tracks navigator.onLine and re-renders on online/offline events. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  return online;
}
